// =============================================================================
// CheckEligibility.js  (NEW PAGE — created for the "Check Eligibility" flow)
// -----------------------------------------------------------------------------
// FLOW CONTEXT:
//   NewProposalDashboard ("Create Blank Proposal")
//        -> /check-eligibility  (THIS PAGE)
//        -> /form (GrantProfileForm)
//
// This page is the intermediate step where the user pastes a grant URL before
// proceeding to the blank proposal form.
//
// This page collects the user-defined proposal name and grant URL before
// opening the grant profile form.

// =============================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Reuse the existing functional NavBar (logo, Dashboard, GrantDB, Sign Out).
import NavBar from '../Components/NavBar';
import GrantAIdeGradientBorder from '../Components/GrantAIdeGradientBorder';
import { FileUploaderDropzone } from '../Components/FileUploader';
import { auth } from '../utils/firebase';
// Shared Genie/Copilot UI in onboarding-only (agent-free) mode.
import GeninieChatPanel from '../Components/GeninieChatPanel';
// SECTION: Material UI dialog components for the non-dismissible usage gate.
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';


const CheckEligibility = () => {
        const [grantProposalName, setGrantProposalName] = useState('');
    const [grantUrl, setGrantUrl] = useState('');
        // The shared uploader owns its upload state. These callbacks retain the
    // selected records for the future eligibility-agent document integration.
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
        const [selectedExistingFiles, setSelectedExistingFiles] = useState([]);
    // Onboarding owns only its selected tab and completion-delay state. It does
    // not call the eligibility agent from this page.
        const [activeOnboardingTab, setActiveOnboardingTab] = useState('eligibility');
        // Build Genie prompts progressively so the panel behaves like a chat history.
    const [onboardingMessages, setOnboardingMessages] = useState([]);
    const [isOnboardingTyping, setIsOnboardingTyping] = useState(true);

        const navigate = useNavigate();
    const location = useLocation();
    // SECTION: This route state is written only after the dashboard pre-flight
    // response. It intentionally stays true until the sole return action runs.
    const isPreflightDenied = location.state?.preflightDenied === true;
        // Client-library documents are owned by this user/client pair. A proposal

    // ID is intentionally not created until the eligibility flow proceeds.
    const clientOrganizationId = location.state?.currentClient?.client_id;
    const uid = auth.currentUser?.uid;

        // Active transfers must finish before the user can enter the next step.
    const isFileProcessing = uploadedDocuments.some((document) => (
        ["pending", "uploading", "parsing"].includes(document.status)
    ));

            // Queue onboarding prompts instead of rendering them immediately. Each
    // completed prompt remains in history, while Genie shows a typing animation
    // before responding with the next one.
    useEffect(() => {
        const hasGrantUrl = grantUrl.trim().length > 0;
        const hasProposalName = grantProposalName.trim().length > 0;
        const messageQueue = [
            { text: 'Please enter a Grant URL', delay: 4000 },
            ...(hasGrantUrl
                ? [{ text: 'Please name your Proposal', delay: 3000 }]
                : []),
            ...(hasGrantUrl && hasProposalName
                ? [{
                    text: 'Optionally Upload or choose a previously uploaded file to improve results',
                    delay: 3000,
                }]
                : []),
            ...(hasGrantUrl && hasProposalName
                ? [{ text: 'You can now start the Eligibility check!', delay: 6000 }]
                : []),
        ];
        const nextMessage = messageQueue.find(({ text }) => (
            !onboardingMessages.includes(text)
        ));

        if (!nextMessage) {
            setIsOnboardingTyping(false);
            return undefined;
        }

                // Do not make Genie appear to respond while the user is still typing.
        // A brief inactivity debounce works even when the input remains focused.
        const isInputDrivenPrompt = [
            'Please name your Proposal',
            'Optionally Upload or choose a previously uploaded file to improve results',
        ].includes(nextMessage.text);
        const typingPauseDelay = isInputDrivenPrompt ? 1500 : 0;
        let messageTimer;

        setIsOnboardingTyping(!isInputDrivenPrompt);
        const typingStartTimer = window.setTimeout(() => {
            setIsOnboardingTyping(true);
            messageTimer = window.setTimeout(() => {
                setOnboardingMessages((messages) => (
                    messages.includes(nextMessage.text)
                        ? messages
                        : [...messages, nextMessage.text]
                ));
            }, nextMessage.delay);
        }, typingPauseDelay);

        return () => {
            window.clearTimeout(typingStartTimer);
            window.clearTimeout(messageTimer);
        };
    }, [grantUrl, grantProposalName, onboardingMessages]);

    // CHANGED: on "Check Eligibility" click, forward to /form.
    // We preserve the incoming navigation state (currentClient / currentProposal
    // passed from new-proposal-dashboard.js) and append the entered grantUrl.
        const handleCheckEligibility = (e) => {
        e.preventDefault();

                // SECTION: Do not allow a denied route to continue through keyboard
        // submission or any programmatic attempt to submit the visible form.
        if (isPreflightDenied || isFileProcessing) {
            return;
        }


        const proposalName = grantProposalName.trim();
        const url = grantUrl.trim();

        if (!proposalName) {
            alert('Please enter a Grant Proposal Name.');
            return;
        }

        if (!url) {
            alert('Please enter a valid Grant URL.');
            return;
        }

        navigate('/form', {
            state: {
                ...location.state, // currentClient + currentProposal from dashboard
                                Grant_Proposal_Name: proposalName,
                grantUrl: url,
                // Preserve selected document references for the planned
                // eligibility-agent document integration.
                eligibilityDocuments: uploadedDocuments,
                eligibilityExistingFiles: selectedExistingFiles,
            },
        });

    };
    
    return (
        // Root: full-height dark canvas. Roboto font per design spec.
        <div
            className="flex flex-col min-h-screen bg-[#0E0126] text-white selection:bg-purple-500/30"
            style={{ fontFamily: 'Roboto, sans-serif' }}
        >
            {/* Header / menu bar — reused as-is from NavBar.jsx (fixed, h-12 = 48px) */}
            <NavBar />

            {/* Keep both outer panels aligned to the 46 px page gutters at every desktop width. */}
            <main className="relative w-full px-[26px] pt-[59px] pb-10 xl:px-[26px] xl:pt-[84px]">
                            <form onSubmit={handleCheckEligibility} className="grid grid-cols-1 gap-7 lg:grid-cols-[243px_minmax(0,415px)_232px] lg:items-start lg:gap-[30px] xl:grid-cols-[minmax(350px,1fr)_minmax(0,698px)_minmax(335px,1fr)] xl:gap-[30px]">
                                <GrantAIdeGradientBorder
                                    className="min-h-[381px] xl:min-h-[740px]"
                                    contentClassName="min-h-[379px] px-[22px] py-[15px] text-[9px] leading-[13px] text-white xl:min-h-[738px] xl:px-[27px] xl:py-[19px] xl:text-[11px] xl:leading-[16px]"
                                                                >
                                    {/* Agent-free Genie guidance for the URL and proposal setup flow. */}
                                    <GeninieChatPanel
                                        activeChatTab={activeOnboardingTab}
                                        onActiveChatTabChange={setActiveOnboardingTab}
                                        onboardingMessages={onboardingMessages}
                                        isTyping={isOnboardingTyping}
                                    />
                                </GrantAIdeGradientBorder>

                                {/* A flex column keeps the submit button aligned with the side panels. */}
                                <section className="min-w-0 lg:flex lg:min-h-[381px] lg:flex-col xl:min-h-[740px]">
                                    <GrantAIdeGradientBorder contentClassName="px-[19px] pb-[30px] pt-[18px] xl:px-[30px] xl:pb-[48px] xl:pt-[30px]">
                                        <label htmlFor="grant-url" className="block text-center text-[9px] font-medium uppercase text-white xl:text-[14px]">
                                            Paste Grant URL
                                        </label>
                                        {/* Gradient frame replaces the former solid #B7DCFF input border. */}
                                        <GrantAIdeGradientBorder
                                            className="mt-[7px] h-[40px] w-full rounded-[3px] xl:mt-[11px]"
                                            contentClassName="h-full rounded-[2px]"
                                        >
                                            <input
                                                id="grant-url"
                                                type="url"
                                                value={grantUrl}
                                                onChange={(e) => setGrantUrl(e.target.value)}
                                                placeholder="HTTP://"
                                                required
                                                className="h-full w-full rounded-[8px] bg-[#14003A] px-[13px] text-[9px] text-white placeholder-[#81718f] outline-none xl:px-[20px] xl:text-[14px]"
                                            />
                                        </GrantAIdeGradientBorder>

                                        <label htmlFor="grant-proposal-name" className="mt-[30px] block text-center text-[9px] font-medium uppercase text-white xl:mt-[48px] xl:text-[14px]">
                                            Name Proposal <span className="ml-1 inline-flex h-[11px] w-[11px] items-center justify-center rounded-full bg-gradient-to-br from-cyan-300 to-purple-600 text-[7px]">i</span>
                                        </label>
                                        {/* Gradient frame replaces the former solid #B7DCFF input border. */}
                                        <GrantAIdeGradientBorder
                                            className="mt-[7px] h-[40px] w-full rounded-[3px] xl:mt-[11px]"
                                            contentClassName="h-full rounded-[2px]"
                                        >
                                            <input
                                                id="grant-proposal-name"
                                                type="text"
                                                value={grantProposalName}
                                                onChange={(e) => setGrantProposalName(e.target.value)}
                                                placeholder="Use a unique grant name e.g. Grant name expiration date"
                                                required
                                                className="h-full w-full rounded-[8px] bg-[#14003A] px-[13px] text-[9px] text-white placeholder-[#81718f] outline-none xl:px-[20px] xl:text-[14px]"
                                            />
                                        </GrantAIdeGradientBorder>
                                    </GrantAIdeGradientBorder>

                                    {/* Shared uploader: its page-specific height keeps status contained without changing other uploader instances. */}
                                    <div className="mt-3 lg:mt-4 xl:mt-5">
                                        <p className="text-center text-[9px] font-medium uppercase text-white xl:text-[14px]">
                                            Upload files to improve results <span className="ml-1 inline-flex h-[12px] w-[12px] items-center justify-center rounded-full bg-gradient-to-br from-cyan-300 to-purple-600 text-[7px]">i</span>
                                        </p>
                                                                                {/* Store PDFs in the reusable client library before a proposal exists. */}
                                        <FileUploaderDropzone
                                            uid={uid}
                                            clientOrganizationId={clientOrganizationId}
                                            onFilesChange={setUploadedDocuments}
                                            clientLibraryMode
                                            parseUploadedProposal={false}
                                            compact
                                            compactHeightClass="h-[255px] xl:h-[278px]"
                                            statusInsideDropzone
                                            className="px-0 py-2"
                                        />
                                    </div>

                                    {/* mt-auto locks this action to the shared bottom edge of the three columns. */}
                                    <button
                                        type="submit"
                                        disabled={isFileProcessing}
                                        className="mx-auto mt-8 block h-[20px] w-[140px] rounded-[5px] border border-[#8d6fbc] bg-[#6c42ba] text-[11px] text-white shadow-[0_1px_5px_rgba(153,111,238,0.5)] transition-colors hover:bg-[#7d54c7] disabled:cursor-not-allowed disabled:opacity-50 lg:mt-auto xl:h-[32px] xl:w-[220px] xl:text-[17px]"
                                    >
                                        {isFileProcessing ? "Uploading Files…" : "Check Eligibility"}
                                    </button>
                                </section>

                                <GrantAIdeGradientBorder
                                    className="min-h-[381px] xl:min-h-[740px]"
                                    contentClassName="min-h-[379px] px-[10px] py-[15px] xl:min-h-[738px] xl:px-[13px] xl:py-[19px]"
                                >
                                    <aside>
                                        {/* The shared existing-file library remains inside this dedicated column. */}
                                            <FileUploaderDropzone
                                            uid={uid}
                                            clientOrganizationId={clientOrganizationId}
                                            showExistingFiles
                                            hideUploader
                                            onExistingFilesChange={setSelectedExistingFiles}
                                            fillExistingFilesPanel
                                            className="px-0 py-0"
                                        />
                                    </aside>
                                </GrantAIdeGradientBorder>
                            </form>
                        </main>


                        {/* Decorative background glow (purely visual, matches design) */}
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none -z-10" />

            {/* SECTION: A failed server pre-flight locks this route. MUI keeps a
                dialog open unless its state changes; no onClose handler means
                backdrop clicks cannot close it, and disableEscapeKeyDown blocks Esc. */}
            <Dialog
                open={isPreflightDenied}
                disableEscapeKeyDown
                aria-labelledby="usage-limit-dialog-title"
                aria-describedby="usage-limit-dialog-description"
                sx={{
                    "& .MuiDialog-paper": {
                        backgroundColor: "#14003A",
                        border: "1px solid #8d6fbc",
                        color: "white",
                    },
                }}
            >
                <DialogTitle id="usage-limit-dialog-title">
                    Proposal creation unavailable
                </DialogTitle>
                <DialogContent id="usage-limit-dialog-description">
                    You have reached your usage limit or your subscription has expired. You cannot create new proposals at this time.
                </DialogContent>
                <DialogActions>
                    {/* The only action replaces the locked route with Dashboard. */}
                    <Button
                        variant="contained"
                        onClick={() => navigate("/proposal-dashboard", { replace: true })}
                        sx={{ backgroundColor: "#6c42ba", color: "white" }}
                    >
                        Return to Dashboard
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
};

export default CheckEligibility;
